// Native speech-to-text for the Capacitor iOS / macOS app.
//
// The Web Speech API (webkitSpeechRecognition) is NOT available inside a native
// WKWebView, so on Apple platforms we route dictation through the native
// @capacitor-community/speech-recognition plugin (SFSpeechRecognizer) instead.
// This module is imported LAZILY and only ever on a native platform, so the
// web/PWA bundle and runtime are completely unaffected.
//
// Native requires these Info.plist keys (added in the iOS project on the Mac):
//   NSMicrophoneUsageDescription
//   NSSpeechRecognitionUsageDescription
//
// Unlike the Web Speech API (which streams incremental FINAL chunks), the native
// plugin's `partialResults` emits matches[0] = the best hypothesis of the WHOLE
// utterance so far. So callers should REPLACE the transcript with each partial,
// not append.

// Loosely typed: the plugin's types resolve at build time, but we keep `any` so
// a version drift in the plugin API never breaks the web type-check.
/* eslint-disable @typescript-eslint/no-explicit-any */
let plugin: any = null

async function load(): Promise<any> {
  if (!plugin) {
    const mod = await import('@capacitor-community/speech-recognition')
    plugin = mod.SpeechRecognition
  }
  return plugin
}

/** Whether on-device speech recognition is available (mic + recognizer present). */
export async function nativeSpeechAvailable(): Promise<boolean> {
  try {
    const p = await load()
    const res = await p.available()
    return !!res?.available
  } catch {
    return false
  }
}

/**
 * Start native dictation. `onPartial` fires with the full running transcript on
 * every update (replace, don't append). Throws if permission is denied so the
 * caller can fall back / surface an error.
 */
export async function startNativeDictation(opts: {
  language?: string
  onPartial: (text: string) => void
}): Promise<void> {
  const p = await load()

  const perm = await p.requestPermissions()
  if (perm?.speechRecognition && perm.speechRecognition !== 'granted') {
    throw new Error('speech-recognition permission denied')
  }

  await p.removeAllListeners()
  await p.addListener('partialResults', (data: { matches?: string[] }) => {
    const text = data?.matches?.[0]
    if (text) opts.onPartial(text)
  })

  await p.start({
    language: opts.language ?? 'en-US',
    partialResults: true,
    popup: false,
  })
}

/** Stop native dictation and detach listeners. Safe to call repeatedly. */
export async function stopNativeDictation(): Promise<void> {
  try {
    const p = await load()
    await p.stop()
    await p.removeAllListeners()
  } catch {
    /* already stopped / not started */
  }
}
